import React from "react";
import { FeatherTrendingUp, FeatherTrendingDown } from "@subframe/core";
import { FeatherInfo } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Tooltip } from "../../../../ui/components/Tooltip";

export type BalanceSheetMetricTrend = "up" | "down" | "neutral";

export interface BalanceSheetMetricCardProps {
  label: string;
  value: string;
  asOfLabel?: string;
  trend?: BalanceSheetMetricTrend;
  trendText?: string;
  infoTooltip?: React.ReactNode;
}

export function BalanceSheetMetricCard({
  label,
  value,
  asOfLabel,
  trend = "neutral",
  trendText,
  infoTooltip,
}: BalanceSheetMetricCardProps) {
  const showTrendIcon = trend === "up" || trend === "down";
  const TrendIcon = trend === "up" ? FeatherTrendingUp : FeatherTrendingDown;
  const trendTextClass =
    trend === "up"
      ? "text-success-700"
      : trend === "down"
        ? "text-error-700"
        : "text-subtext-color";
  const trendPillClass =
    trend === "up"
      ? "bg-success-50 ring-success-200"
      : trend === "down"
        ? "bg-error-50 ring-error-200"
        : "bg-neutral-50 ring-neutral-200";

  return (
    <div className="group flex h-full min-h-[156px] grow shrink-0 basis-0 flex-col items-stretch rounded-xl border border-solid border-neutral-200 bg-white px-5 py-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-body font-body text-subtext-color">
              {label}
            </span>
            {infoTooltip ? (
              <SubframeCore.Tooltip.Provider>
                <SubframeCore.Tooltip.Root>
                  <SubframeCore.Tooltip.Trigger asChild={true}>
                    <div
                      className="flex h-6 w-6 flex-none cursor-help items-center justify-center rounded-full bg-neutral-100 transition-colors group-hover:bg-neutral-200"
                      aria-label={`${label} info`}
                    >
                      <FeatherInfo className="text-body font-body text-neutral-500" />
                    </div>
                  </SubframeCore.Tooltip.Trigger>
                  <SubframeCore.Tooltip.Portal>
                    <SubframeCore.Tooltip.Content
                      side="bottom"
                      align="start"
                      sideOffset={8}
                      asChild={true}
                    >
                      <Tooltip className="max-w-[360px] whitespace-pre-wrap">
                        {infoTooltip}
                      </Tooltip>
                    </SubframeCore.Tooltip.Content>
                  </SubframeCore.Tooltip.Portal>
                </SubframeCore.Tooltip.Root>
              </SubframeCore.Tooltip.Provider>
            ) : null}
          </div>
          {asOfLabel ? (
            <div className="mt-0.5 text-caption font-caption text-neutral-500 leading-tight">
              As of {asOfLabel}
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-3 text-heading-1 font-heading-1 text-default-font tracking-tight">
        {value}
      </div>

      <div className="mt-auto pt-3">
        {trendText ? (
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-caption font-caption ring-1 ring-inset ${trendPillClass} ${trendTextClass}`}
          >
            {showTrendIcon ? <TrendIcon className={trendTextClass} /> : null}
            <span className="whitespace-nowrap">{trendText}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
